/**
 * useAsconSchedule — 경원(아스콘) 그날 수주·계근을 중앙 스케쥴 시트·타임라인·MO 카드에 넘길 모양으로 바꾼다.
 *
 * 출하조회(AsconDispatch)와 출하실적 일별 상세(ShipmentStats/ascon/AsconDayModal)가 같은 칸·같은 칩을 쓰도록 한 곳에 둔다.
 *   cardRows  수주 → RqcShipment 모양(asconOrdersToCardRows)
 *   barMeta   수주별 계근 시각 → 타임라인 막대·점(metaOverride, 중앙 송장 캐시 안 읽음)
 *   extraCols 온도 칸 / specCols 규격·골재 칸(배합 칸 자리) / renderSpec MO 카드 규격 칩
 */
import { useCallback, useMemo } from "react";
import {
  asconOrdersToCardRows,
  avgTempC,
  ticketsOfOrder,
  type AsconOrder,
  type AsconTicket,
} from "@/domain/asconShipment";
import type { RqcShipment } from "@/domain/rqc";
import type { ShipmentBarMeta } from "@/hooks/useDailyShipmentDetails";
import type { ScheduleExtraCol } from "../ScheduleTablePreview";
import { fmt } from "../lib";
import { AsconAggChip, AsconSpecChip, AsconSpecPair } from "./AsconSpecChips";
import { asconSpecColWidth } from "./specWidth";

/** "14:20" → 860 */
function hhmmToMin(t: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** 수주별 계근 시각 → 타임라인 막대·점 (중앙 useDailyShipmentDetails 와 같은 모양) */
function barMetaOf(ts: AsconTicket[]): ShipmentBarMeta | undefined {
  const times = ts.map((t) => hhmmToMin(t.time)).filter((v): v is number => v != null).sort((a, b) => a - b);
  if (!times.length) return undefined;
  const first = times[0], last = times[times.length - 1];
  return {
    firstMin: first,
    lastMin: last,
    count: times.length,
    avgIntervalMin: times.length >= 2 ? (last - first) / (times.length - 1) : null,
    times,
  };
}

export function useAsconSchedule(orders: AsconOrder[], tickets: AsconTicket[]) {
  const cardRows = useMemo(() => asconOrdersToCardRows(orders, tickets), [orders, tickets]);
  // 수주 id → 그 수주 계근 / 수주 원본 (시트 추가 열·타임라인·모달)
  const ticketsById = useMemo(
    () => new Map(orders.map((o) => [o.id, ticketsOfOrder(o, tickets)])),
    [orders, tickets],
  );
  const orderById = useMemo(() => new Map(orders.map((o) => [o.id, o])), [orders]);
  const barMeta = useMemo(() => {
    const m = new Map<number, ShipmentBarMeta>();
    for (const r of cardRows) {
      const meta = r.sc ? barMetaOf(ticketsById.get(r.id) ?? []) : undefined;
      if (r.sc && meta) m.set(r.sc, meta);
    }
    return m;
  }, [cardRows, ticketsById]);

  const extraCols = useMemo<ScheduleExtraCol[]>(() => [
    {
      key: "temp", label: "온도", width: 56, align: "num", title: "평균 출하 온도(℃)",
      render: (r) => {
        const t = avgTempC(ticketsById.get(r.id) ?? []);
        return t != null ? `${fmt(t, 0)}℃` : "—";
      },
    },
  ], [ticketsById]);

  // 배합 칸 자리 = 규격(칩) · 골재(칩) 두 칸 (10-22 사용자 지정). 규격 칸 폭은 그날 가장 긴 규격에 맞춤 — 등급 있을 때만 넓어짐.
  // 정렬은 규격 머리 = 중앙 배합 정렬 키("표층용(WC-2)1등급 · 13mm" 글자순)
  const specCols = useMemo<ScheduleExtraCol[]>(() => {
    const cell = (r: RqcShipment, C: typeof AsconSpecChip) => {
      const o = orderById.get(r.id);
      return o ? <C order={o} /> : <span className="dispatch-row-mix">—</span>;
    };
    return [
      { key: "spec", label: "규격", width: asconSpecColWidth(orders), sortKey: "mix", render: (r) => cell(r, AsconSpecChip) },
      { key: "agg", label: "골재", width: 68, align: "center", render: (r) => cell(r, AsconAggChip) },
    ];
  }, [orderById, orders]);
  // MO 카드는 규격·골재 칩 한 줄(카드 칩 모양)
  const renderSpec = useCallback((r: RqcShipment, chipClass: string) => {
    const o = orderById.get(r.id);
    return o ? <AsconSpecPair order={o} chipClass={chipClass} /> : <span className="dispatch-row-mix">—</span>;
  }, [orderById]);

  return { cardRows, ticketsById, orderById, barMeta, extraCols, specCols, renderSpec };
}
